import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { CURRENT_STATE_PROJECT_KEY, isModelNode, Linqset, MODEL_TYPE, NODE_TYPE, TUTORIAL_MODEL_KEY } from '../../common/linqset-model';
import { FUNCTION, FunctionService } from '../../model-services/function.service';
import { SCREEN } from '../../layouts/layouts-enums';
import { OrganisationService } from '../../organisation/organisation.service';
import { constructHelp, constructShortCuts } from './search-utils';
import { DEFAULT_URL, INSIGHTS_URL, SKETCH_URL } from '../../app-urls';
import { ModelService } from '../../organisation/models/model-service';
import { GraphProcessingService } from '../../model-services/graph-processing.service';
import { Organisation } from '../../common/organisation';
import { LENSES } from '../../lenses/lens';
import { nodeTypeName } from '../../canvas/node-tooltip';
import { HelpGuidesService } from '../../help/guides/help-guides.service';
import { HelpGuideSummary } from '../../common/help-model';
import { analysisView, EMPTY_GROUPS, GROUP_ORDER, GroupKind, offersFunction, offersShortcut, rankResults, SearchContext, searchContext, suggestedFunctions } from './search-context';

enum SEARCH_TYPE {
    NODE = "Nodes",
    MODEL = "Models",
    PROJECT = "Projects",
    FUNCTION = "Functions",
    PAGE = "Go to",
    SHORT_CUTS = "Short cuts",
    HELP = "Help"
}

const KIND: Record<SEARCH_TYPE, GroupKind> = {
    [SEARCH_TYPE.NODE]: 'nodes', [SEARCH_TYPE.MODEL]: 'models', [SEARCH_TYPE.PROJECT]: 'projects', [SEARCH_TYPE.FUNCTION]: 'functions',
    [SEARCH_TYPE.PAGE]: 'pages', [SEARCH_TYPE.SHORT_CUTS]: 'shortcuts', [SEARCH_TYPE.HELP]: 'help',
};

// a node in a screen's own graph (an analysis view's steps, roles or systems), for search to find and the screen to show
export interface GraphSearchNode {
    key:string
    name:string
    description:string
    type?:NODE_TYPE
}

export interface SearchResult {
    _key:string
    icon:string
    type:NODE_TYPE
    name:string
    description:string
    disabled:boolean
}

interface SearchGroup {
    group: SEARCH_TYPE;
    name: string,
    results: any[];
    more?: number;          // results left out to keep the list short
    current?: boolean;      // a project's models, when it's the workspace being looked at
}

// the most results shown for each group; typing more narrows them down
const MAX_RESULTS = 25
// with nothing typed yet, a few from each of the groups likely to be wanted
const MAX_EMPTY_RESULTS = 6

// what the search box says it finds, where it's used
const LABELS: Record<SearchContext, string> = {
    model: 'Search nodes, functions, pages and help',
    insights: 'Search nodes, functions, pages and help',
    analysis: 'Search pages, models, nodes and help',
    home: 'Search models, projects, pages and help',
    other: 'Search pages, models and help',
};

@Component({
    selector: 'app-search',
    templateUrl: './search.html',
    styleUrls: ['./search.css'],
    standalone: false
})
export class SearchComponent  implements OnInit, OnChanges, OnDestroy {
    @Input() organisation:Organisation
    @Input() linqset:Linqset
    @Input() screen:SCREEN|undefined         // the screen it searches from: what it offers depends on it
    @Input() graphNodes:GraphSearchNode[]|undefined   // the nodes the screen's own graph shows, in place of the linqset's

    @ViewChild('searchInput', {read: ElementRef}) private searchInput: ElementRef;
    @ViewChild('searchOverlay') seachOverlayTemplate: TemplateRef<any>;

    isOpen = false;
    searchKey:string = ""
    overlayRef:OverlayRef
    private subs:Subscription[] = []

    searchGroups:SearchGroup[] = []
    filteredGroups:SearchGroup[]
    activeIndex = -1        // the result chosen with the arrow keys, counting through all groups
    options:{group:SearchGroup, result:any}[] = []

    private helpGuides:HelpGuideSummary[] = []

    @Output() functionEvent = new EventEmitter<{function:FUNCTION, value?:any}>()
    @Output() selectNodeEvent = new EventEmitter<string>()

    constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef, private functionService:FunctionService,
        private organisationService:OrganisationService, private router:Router, private guidesService:HelpGuidesService,
        private modelService:ModelService, private graphProcessingService:GraphProcessingService) {}

    get context():SearchContext {
        return searchContext(this.screen)
    }

    get label() {
        return this.graphNodes ? 'Search nodes, pages, models and help' : LABELS[this.context]
    }

    // with nothing typed yet, what to try
    get emptyHint() {
        return this.context === 'model' || this.context === 'insights'
            ? 'Type to find a node by name, a function, a page, a shortcut or a help guide.'
            : 'Type to find a model, a page, a function or a help guide.'
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe())
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["organisation"] || changes["screen"]) this.buildGroups()
        else if (changes["linqset"] || changes["graphNodes"]) this.reloadLinqset()
    }

    ngOnInit() {
        this.subs.push(this.graphProcessingService.changeEvent.subscribe(() => this.reloadLinqset()))
        // the analysis views share a screen: moving between them changes the functions and pages offered
        this.subs.push(this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.reloadPagesAndFunctions()))
        this.buildGroups()
        this.guidesService.guides().then(guides => {
            this.helpGuides = guides
            this.reloadHelp()
        }).catch(() => undefined)
    }

    @HostListener('keydown.esc', ['$event'])
    onEsc(event: Event) {
        this.close()
    }

    // Ctrl+K (or Cmd+K) jumps to the search box from anywhere
    @HostListener('document:keydown', ['$event'])
    onShortcut(event:KeyboardEvent) {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
            event.preventDefault()
            this.searchInput?.nativeElement.focus()
            this.open(event)
        }
    }

    // arrow keys move through the results and Enter opens the chosen one
    onKeydown(event:KeyboardEvent) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            if (!this.isOpen) this.open(event)
            if (this.options.length === 0) return
            const step = event.key === 'ArrowDown' ? 1 : -1
            this.activeIndex = (this.activeIndex + step + this.options.length) % this.options.length
            setTimeout(() => document.getElementById(this.optionId(this.activeIndex))?.scrollIntoView({block:'nearest'}))
        }else if (event.key === 'Enter') {
            const option = this.options[this.activeIndex] || (this.options.length === 1 ? this.options[0] : undefined)
            if (option) {
                event.preventDefault()
                this.select(option.group, option.result)
            }
        }
    }

    optionId(index:number) {
        return `search-option-${index}`
    }

    // the position of a result in the list the arrow keys move through
    optionIndex(group:SearchGroup, result:any) {
        return this.options.findIndex(o => o.group === group && o.result === result)
    }

    open(keyEvent:any) {
        if (keyEvent?.key === 'Escape' || keyEvent?.key === 'ArrowDown' && this.isOpen || keyEvent?.key === 'ArrowUp' && this.isOpen || keyEvent?.key === 'Enter') return
        this.searchResults()
        if (!this.isOpen) {
            this.isOpen = true
            const positionStrategy = this.overlay.position()
                .flexibleConnectedTo(this.searchInput)
                .withPositions([{
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
            }]);

            this.overlayRef = this.overlay.create({
                hasBackdrop: true,
                backdropClass: "backdrop-class",
                positionStrategy,
                maxWidth: '95vw',
                scrollStrategy: this.overlay.scrollStrategies.reposition()
            });

            this.overlayRef.attach(new TemplatePortal(this.seachOverlayTemplate, this.viewContainerRef, {}));

            this.overlayRef.backdropClick().subscribe(() => this.close());
        }
    }

    close() {
        this.isOpen = false
        this.activeIndex = -1
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
     }

    // the groups in the order they matter here, each with its matches best first. Typing a group's name lists all of it;
    // with nothing typed, a few from the groups likely to be wanted
    searchResults() {
        const query = (this.searchKey || '').trim()
        // a screen with its own graph to search finds its nodes first
        const order = this.graphNodes?.length ? (['nodes', ...GROUP_ORDER[this.context].filter(k => k !== 'nodes')] as GroupKind[]) : GROUP_ORDER[this.context]
        const shown = query ? order : EMPTY_GROUPS[this.context]
        const rank = (g:SearchGroup) => {
            const index = order.indexOf(KIND[g.group])
            return index < 0 ? order.length : index
        }
        this.filteredGroups = this.searchGroups
            .filter(g => shown.includes(KIND[g.group]))
            .sort((a, b) => rank(a) - rank(b) || Number(!!b.current) - Number(!!a.current))
            .map(group => {
                const whole = !!query && group.name.toLowerCase().startsWith(query.toLowerCase())
                // with nothing typed, the functions most wanted here rather than the first in the list
                const results = !query && group.group === SEARCH_TYPE.FUNCTION ? suggestedFunctions(group.results, this.context)
                    : whole ? group.results : rankResults(group.results, query)
                const max = query ? MAX_RESULTS : MAX_EMPTY_RESULTS
                return {...group, results: results.slice(0, max), more: query ? Math.max(0, results.length - max) : 0}
            })
            .filter(group => group.results.length > 0)
        this.options = this.filteredGroups.flatMap(group => group.results.map(result => ({group, result})))
        this.activeIndex = this.options.length > 0 && query ? 0 : -1
    }

    private buildGroups() {
        this.searchGroups = []
        this.reloadOrganisation()
        this.reloadLinqset()
        this.reloadPagesAndFunctions()
        const shortcuts = constructShortCuts().filter(s => offersShortcut(s._key, this.context))
        if (shortcuts.length) this.searchGroups.push({group:SEARCH_TYPE.SHORT_CUTS, name: SEARCH_TYPE.SHORT_CUTS.toString(), results: shortcuts})
        this.reloadHelp()
    }

    // the help guides, once their list has come from the server, and the tutorials
    private reloadHelp() {
        this.searchGroups = this.searchGroups.filter(sg => sg.group !== SEARCH_TYPE.HELP)
        this.searchGroups.push({group:SEARCH_TYPE.HELP, name: SEARCH_TYPE.HELP.toString(), results: constructHelp(this.helpGuides)})
    }

    // the organisation's models: the current state's, then each project's, the project being looked at first
    reloadOrganisation() {
        this.searchGroups = this.searchGroups.filter(sg => sg.group !== SEARCH_TYPE.MODEL && sg.group !== SEARCH_TYPE.PROJECT)
        if (!this.organisation) return
        const models = (this.organisation.models || []).filter(m => !m.deletedDate)
        const workspaceKey = this.linqset?.workspaceKey
        this.searchGroups.push({group:SEARCH_TYPE.MODEL, name: SEARCH_TYPE.MODEL.toString(),
            results: models.filter(m => m.projectKey === CURRENT_STATE_PROJECT_KEY).map(m => ({...m, icon:"schema", description: m.description || m.function || ''}))})
        ;(this.organisation.projects || []).forEach(p => {
            this.searchGroups.push({group:SEARCH_TYPE.PROJECT, name: p.name, current: p._key === workspaceKey,
                results: models.filter(m => m.projectKey === p._key).map(m => ({...m, icon:"work", description: `In the project ${p.name}`}))})
        })
    }

    // the nodes of what's being looked at, each saying what it is and which model it's in. Where no graph can select them,
    // only nodes in a model are offered, and they open there
    reloadLinqset() {
        this.searchGroups = this.searchGroups.filter(sg => sg.group !== SEARCH_TYPE.NODE)
        if (this.graphNodes) {
            const results = this.graphNodes.filter(n => n.name).map(n => ({_key:n.key, name:n.name, description:n.description, type:n.type, disabled:false}))
            if (results.length) this.searchGroups.push({group:SEARCH_TYPE.NODE, name: SEARCH_TYPE.NODE.toString(), results})
        }else if (this.linqset) {
            const modelNames = new Map((this.organisation?.models || this.organisationService.organisation?.models || []).map(m => [m._key, m.name]))
            const opensInModel = !this.selectsNodes
            const results = this.linqset.nodes
                .filter(n => n.name && (!opensInModel || (isModelNode(n.superType) && modelNames.has(n.modelKey))))
                .map(n => {
                    const model = isModelNode(n.superType) ? modelNames.get(n.modelKey) : undefined
                    return {_key:n._key, name:n.name, description: nodeTypeName(n.type, n.superType) + (model ? ` · ${model}` : ''), type:n.type, modelKey:n.modelKey, disabled:false}
                })
            if (results.length) this.searchGroups.push({group:SEARCH_TYPE.NODE, name: SEARCH_TYPE.NODE.toString(), results})
        }
        this.reloadPagesAndFunctions()
    }

    // a graph on the screen selects the node chosen: the model's canvas, the insights graph, the home screen's, or an
    // analysis view's own graph
    private get selectsNodes() {
        return !!this.graphNodes || this.context === 'model' || this.context === 'insights' || this.context === 'home'
    }

    // the functions that work here, and the pages to go to from here
    private reloadPagesAndFunctions() {
        this.searchGroups = this.searchGroups.filter(sg => sg.group !== SEARCH_TYPE.FUNCTION && sg.group !== SEARCH_TYPE.PAGE)
        const functions = this.functions()
        if (functions.length) this.searchGroups.push({group:SEARCH_TYPE.FUNCTION, name: SEARCH_TYPE.FUNCTION.toString(), results: functions})
        const pages = this.pages()
        if (pages.length) this.searchGroups.push({group:SEARCH_TYPE.PAGE, name: SEARCH_TYPE.PAGE.toString(), results: pages})
        if (this.isOpen) this.searchResults()
    }

    private functions() {
        const all = Object.values(this.functionService.getFunctionMap(true, this.linqset)) as any[]
        const context = {context: this.context, view: analysisView(this.router.url), emptyModel: this.isEmptyModel()}
        return all.filter(f => offersFunction(f, context))
    }

    // a process model with nothing in it yet, which AI can build
    private isEmptyModel() {
        const linqset = this.linqset
        if (!linqset || !linqset.isProcessModel() || linqset.isTutorial()) return false
        return !linqset.nodes.some(n => isModelNode(n.superType) ? n.modelKey === linqset.modelKey : true)
    }

    // the organisation's home, its insights, and each view of the analysis; not the page it's on
    private pages() {
        const organisationKey = this.organisation?._key || this.organisationService.organisation?._key
        if (!organisationKey) return []
        const here = (this.router.url || '').split(/[?#]/)[0].replace(/\/+$/, '')
        return [
            {_key: DEFAULT_URL(organisationKey), icon: 'home', name: 'Home', description: "The organisation's models, projects and what to do first"},
            ...LENSES.map(l => ({_key: l.url(organisationKey), icon: l.icon, name: `Analysis: ${l.label}`, description: l.description})),
            {_key: INSIGHTS_URL(organisationKey), icon: 'insights', name: 'Insights screen', description: "The organisation's insights, filtered by models, tags or nodes"},
        ].filter(p => p._key.replace(/\/+$/, '') !== here)
    }

    select(group:SearchGroup, result:any) {
        switch (group.group) {
            case SEARCH_TYPE.FUNCTION:
                this.functionEvent.emit({function:result._key})
                break
            case SEARCH_TYPE.PAGE:
                this.router.navigateByUrl(result._key)
                break
            case SEARCH_TYPE.PROJECT: {
                // opens the project's model, or offers to request access if the user has none
                const model = this.organisationService.organisation.models.find(m => m._key === result._key && m.projectKey === result.projectKey)
                if (model) this.modelService.editModel(this.organisationService.organisation, model, result.projectKey)
                break
            }
            case SEARCH_TYPE.MODEL:
                const model = this.organisationService.organisation.models.find(m => m._key === result._key)
                if (model) this.modelService.editModel(this.organisationService.organisation, model, this.linqset?.workspaceKey)
                break
            case SEARCH_TYPE.NODE:
                if (this.selectsNodes) this.selectNodeEvent.emit(result._key)
                else this.openNodeInModel(result)
                break
            case SEARCH_TYPE.HELP:
                if (result.icon === "school")
                    this.router.navigateByUrl(SKETCH_URL(this.organisationService.organisation._key, TUTORIAL_MODEL_KEY, MODEL_TYPE.PROCESS))
                else this.guidesService.open(result._key)

                break

        }
        this.searchKey = ''
        this.close()
        this.searchInput?.nativeElement.blur()
    }

    // a node in its model, selected there
    private openNodeInModel(result:{_key:string, modelKey:string}) {
        const organisationKey = this.organisation?._key || this.organisationService.organisation?._key
        if (!organisationKey || !result.modelKey) return
        this.router.navigate([SKETCH_URL(organisationKey, result.modelKey, MODEL_TYPE.PROCESS, this.linqset?.workspaceKey)], {queryParams: {nodeKey: result._key}})
    }
}
